import { Injectable } from '@angular/core';
import { addressString } from '@core/util/address';
import {
	clientIsPerson,
	clientLegalName,
	getSignatories,
	isGuarantor,
} from '@core/util/client';
import { GetDateObject } from '@core/util/datetime';
import { formatCentsToDollars } from '@core/util/money/centsToDollars';
import { getPrimaryParcel } from '@core/util/parcels';
import { isSet } from '@core/util/value';
import { NameFormattingSettings } from '@models/businessSettings/settings';
import { Business, BusinessAddress } from '@models/businesses';
import { IdentificationPriority } from '@models/identification';
import { MortgagePriority } from '@models/encumbrance';
import {
	BusinessMatterClient,
	BusinessMatterClientWithAssociations,
	CalculatedLandTransferTax,
	ClientsCapacity,
	Matter,
	MatterTitle,
	PersonMatterClient,
	PersonMatterClientWithAssociations,
} from '@models/matter';
import {
	MatterMortgageWithAssociations,
	NetAmountType,
} from '@models/mortgage';
import { TitleInsurancePolicyType } from '@models/title-insurance';
import { AllAdjustmentsCalculated } from '@state/matter/adjustments/adjustments.selectors';
import { format } from 'date-fns';
import { cloneDeep } from 'lodash';
import { CalculateNetAdvance } from 'src/app/shared/services/financialTotals/financial-totals.service';
import { BaseFormMapper } from '../../base-form-mapper';
import { getMortgageFinancialInstitutionLender } from '@core/util/mortgage';
import { joinNames } from '@core/util/string';

@Injectable({
	providedIn: 'root',
})
export class TD_FINAL_REPORT_Mapper extends BaseFormMapper<TD_FINAL_REPORT> {
	CloserToFormMappings = Mappings;

	mergeFormData(
		formData: TD_FINAL_REPORT,
		matter: Matter,
		nameFormattingSettings: NameFormattingSettings,
		sellers: (PersonMatterClient | BusinessMatterClient)[],
		buyers: (PersonMatterClient | BusinessMatterClient)[],
		adjustments: AllAdjustmentsCalculated,
		business: Business,
		matterTitles: MatterTitle[],
		matterClients: (
			| PersonMatterClientWithAssociations
			| BusinessMatterClientWithAssociations
		)[],
		lttCalculated: CalculatedLandTransferTax,
		businessAddress: BusinessAddress,
		otherParties: PersonMatterClient[],
		clientsCapacity: ClientsCapacity,
		mortgage: MatterMortgageWithAssociations,
	): TD_FINAL_REPORT {
		const _formData = formData ? cloneDeep(formData) : ({} as TD_FINAL_REPORT);

		const financialInstitution =
			getMortgageFinancialInstitutionLender(mortgage);

		if (!isSet(_formData?.todaysDate)) {
			_formData.todaysDate = format(new Date(), 'MMMM dd, yyyy');
		}

		if (matter?.lawyerAssigned?.name && !isSet(_formData?.lawyersName)) {
			_formData.lawyersName = matter.lawyerAssigned.name;
		}

		if (
			financialInstitution?.branch?.transitNumber &&
			!isSet(_formData?.branchTransitNumber)
		) {
			_formData.branchTransitNumber = financialInstitution.branch.transitNumber;
		}

		if (mortgage?.referenceNumber && !isSet(_formData?.lenderReferenceNumber)) {
			_formData.lenderReferenceNumber = mortgage.referenceNumber;
		}

		if (matterClients?.length && !isSet(_formData?.clientsNames1)) {
			const FilteredMatterClients = matterClients.filter(
				(client) => !isGuarantor(client),
			);
			_formData.clientsNames1 = joinNames(
				FilteredMatterClients.map((client) =>
					clientLegalName(client, nameFormattingSettings, {
						applyFormatting: false,
					}),
				),
			);
		}

		if (mortgage?.guarantors && !isSet(_formData?.guarantorsPage1)) {
			const FilteredMatterClients = matterClients?.filter(
				(client) =>
					isGuarantor(client) && mortgage.guarantors.includes(client.id),
			);
			_formData.guarantorsPage1 = joinNames(
				FilteredMatterClients.map((client) =>
					clientLegalName(client, nameFormattingSettings, {
						applyFormatting: false,
					}),
				),
			);
		}

		const PrimaryTitle = getPrimaryParcel(matterTitles);
		if (
			PrimaryTitle?.description &&
			!isSet(_formData?.briefLegalDescriptionOfPropertyProperty1)
		) {
			_formData.briefLegalDescriptionOfPropertyProperty1 =
				PrimaryTitle.description;
		}

		if (
			matter?.propertyDetails?.address &&
			!isSet(_formData?.municipalAddressOfPropertyProperty1)
		) {
			_formData.municipalAddressOfPropertyProperty1 = addressString(
				matter.propertyDetails.address,
			);
		}

		if (
			matter?.propertyDetails?.address?.city &&
			!isSet(_formData?.municipalityCityOrTown)
		) {
			_formData.municipalityCityOrTown = `City of ${matter.propertyDetails.address.city}`;
		}

		if (
			matter?.propertyDetails?.rollNumber &&
			!isSet(_formData?.taxRollNumber)
		) {
			_formData.taxRollNumber = matter.propertyDetails.rollNumber;
		}

		if (mortgage?.registrationDate && !isSet(_formData?.registrationDate)) {
			_formData.registrationDate = mortgage.registrationDate;
		}

		if (mortgage?.registrationNumber && !isSet(_formData?.registrationNumber)) {
			_formData.registrationNumber = mortgage.registrationNumber;
		}

		if (matter?.propertyDetails?.lro?.selected && !isSet(_formData?.lROName)) {
			_formData.lROName = matter.propertyDetails.lro.selected.name;
		}

		if (mortgage?.priority && !isSet(_formData?.mortgagePriority1)) {
			_formData.mortgagePriority1 = MortgagePriority.find(
				(option) => mortgage.priority === option.value,
			)?.label;
		}

		if (mortgage?.priority && !isSet(_formData?.mortgagePriority2)) {
			_formData.mortgagePriority2 = MortgagePriority.find(
				(option) => mortgage.priority === option.value,
			)?.label;
		}

		if (
			matter?.titleInsurance?.companyName &&
			!isSet(_formData?.titleInsuranceCompanyName)
		) {
			_formData.titleInsuranceCompanyName = matter.titleInsurance.companyName;
		}

		const lenderPolicy = matter?.titleInsurance?.policies?.find(
			(policy) =>
				policy.type === TitleInsurancePolicyType.LENDER &&
				policy.priority === mortgage?.priority,
		);
		if (lenderPolicy && !isSet(_formData?.policyNumber)) {
			_formData.policyNumber = lenderPolicy.number;
		}

		if (
			matter?.titleInsurance?.effectiveDate &&
			!isSet(_formData?.issuedOnDate)
		) {
			_formData.issuedOnDate = format(
				GetDateObject(matter.titleInsurance.effectiveDate),
				'MMMM dd, yyyy',
			);
		}

		if (!isSet(_formData?.nameOfInsured)) {
			_formData.nameOfInsured = 'The Toronto-Dominion Bank';
		}

		if (mortgage?.amount && !isSet(_formData?.insuredAmount)) {
			_formData.insuredAmount = formatCentsToDollars(mortgage.amount, false);
		}

		if (mortgage?.priority && !isSet(_formData?.mortgagePriority3)) {
			_formData.mortgagePriority3 = MortgagePriority.find(
				(option) => mortgage.priority === option.value,
			)?.label;
		}

		if (
			matter?.propertyDetails?.interestEstate &&
			!isSet(_formData?.leasehold) &&
			!isSet(_formData?.feeSimple) &&
			!isSet(_formData?.otherCheckboxCoveredInPolicy)
		) {
			_formData.leasehold = 'Off';
			_formData.feeSimple = 'Off';
			_formData.otherCheckboxCoveredInPolicy = 'Off';
			_formData.otherTextCoveredInPolicy = '';
			if (matter.propertyDetails.interestEstate === 'Fee Simple') {
				_formData.feeSimple = 'On';
			} else if (matter.propertyDetails.interestEstate === 'Leasehold') {
				_formData.leasehold = 'On';
			} else {
				_formData.otherCheckboxCoveredInPolicy = 'On';
				_formData.otherTextCoveredInPolicy =
					matter.propertyDetails.interestEstate;
			}
		}

		if (matterClients?.length && !isSet(_formData?.clientsName3)) {
			const FilteredMatterClients = matterClients.filter(
				(client) => !isGuarantor(client),
			);
			_formData.clientsName3 = joinNames(
				FilteredMatterClients.map((client) =>
					clientLegalName(client, nameFormattingSettings, {
						applyFormatting: false,
					}),
				),
			);
		}

		const netAdvanceTotal = CalculateNetAdvance(mortgage).total;
		if (netAdvanceTotal && !isSet(_formData?.netAdvanceAmount)) {
			_formData.netAdvanceAmount = formatCentsToDollars(netAdvanceTotal, false);
		}

		const MortgageInsurancePremium = mortgage?.netAdvancedAmounts.filter(
			(v) => v.type === NetAmountType.INSURANCE_PREMIUM,
		);
		if (
			MortgageInsurancePremium.length &&
			!isSet(_formData?.mortgageInsurancePremium)
		) {
			_formData.mortgageInsurancePremium = formatCentsToDollars(
				MortgageInsurancePremium[0].amount,
				false,
			);
		}

		const InsuranceApplicationFee = mortgage?.netAdvancedAmounts.filter(
			(v) => v.type === NetAmountType.INSURANCE_APPLICATION_FEE,
		);
		if (
			InsuranceApplicationFee.length &&
			!isSet(_formData?.mortgageApplicationFee)
		) {
			_formData.mortgageApplicationFee = formatCentsToDollars(
				InsuranceApplicationFee[0].amount,
				false,
			);
		}

		const AppraisalFee = mortgage?.netAdvancedAmounts.filter(
			(v) => v.type === NetAmountType.APPRAISAL_FEE,
		);
		if (AppraisalFee.length && !isSet(_formData?.appraisalFee)) {
			_formData.appraisalFee = formatCentsToDollars(
				AppraisalFee[0].amount,
				false,
			);
		}

		if (
			matter?.fireInsurance?.fireInsuranceCompany?.name &&
			!isSet(_formData?.fireInsuranceCompanyName)
		) {
			_formData.fireInsuranceCompanyName =
				matter.fireInsurance.fireInsuranceCompany.name;
		}

		if (
			matter?.fireInsurance?.policyNumber &&
			!isSet(_formData?.fireInsurancePolicyNumber)
		) {
			_formData.fireInsurancePolicyNumber = matter.fireInsurance.policyNumber;
		}

		if (
			matter?.fireInsurance?.coverageAmount &&
			!isSet(_formData?.fireInsuranceCoverageAmount)
		) {
			_formData.fireInsuranceCoverageAmount = formatCentsToDollars(
				matter.fireInsurance.coverageAmount,
				false,
			);
		}

		if (
			matter?.fireInsurance?.expiryDate &&
			!isSet(_formData?.fireInsuranceExpiryDate)
		) {
			_formData.fireInsuranceExpiryDate = format(
				GetDateObject(matter.fireInsurance.expiryDate),
				'MMMM dd, yyyy',
			);
		}

		if (!isSet(_formData?.provinceName)) {
			_formData.provinceName = 'Ontario';
		}

		if (matterClients?.length) {
			const Contacts = getSignatories(matterClients);
			// select a contact for single source method
			// if they have either driver's license or passport as id
			const SSContacts = Contacts.filter(
				(c) =>
					c.identification?.[IdentificationPriority.PRIMARY]?.type ===
						'driverLicense' ||
					c.identification?.[IdentificationPriority.SECONDARY]?.type ===
						'driverLicense' ||
					c.identification?.[IdentificationPriority.PRIMARY]?.type ===
						'passport' ||
					c.identification?.[IdentificationPriority.SECONDARY]?.type ===
						'passport',
			);

			const ClientSSProperties = [
				{
					dateIdVerified: 'dateIdVerified1',
					dateOfBirth: 'dateOfBirth1',
					ssFullNameOnId: 'ssFullNameOnId1',
					identificationNumber: 'identificationNumber1',
					typeOfPhotoIdentification: 'typeOfPhotoIdentification1',
					expiryDateIfApplicable: 'expiryDateIfApplicable1',
					issuingCountry: 'issuingCountry1',
					issuingJurisdiction: 'issuingJurisdiction1',
				},
				{
					dateIdVerified: 'dateIdVerified2',
					dateOfBirth: 'dateOfBirth2',
					ssFullNameOnId: 'ssFullNameOnId2',
					identificationNumber: 'identificationNumber2',
					typeOfPhotoIdentification: 'typeOfPhotoIdentification2',
					expiryDateIfApplicable: 'expiryDateIfApplicable2',
					issuingCountry: 'issuingCountry2',
					issuingJurisdiction: 'issuingJurisdiction2',
				},
				{
					dateIdVerified: 'dateIdVerified3',
					dateOfBirth: 'dateOfBirth3',
					ssFullNameOnId: 'ssFullNameOnId3',
					identificationNumber: 'identificationNumber3',
					typeOfPhotoIdentification: 'typeOfPhotoIdentification3',
					expiryDateIfApplicable: 'expiryDateIfApplicable3',
					issuingCountry: 'issuingCountry3',
					issuingJurisdiction: 'issuingJurisdiction3',
				},
			];
			for (let index = 0; index < SSContacts.length && index <= 2; index++) {
				const client = SSContacts[index];
				// select one identification
				// identification has to be driverLicense or passport
				const Identification =
					client.identification?.[IdentificationPriority.PRIMARY]?.type ===
						'driverLicense' ||
					client.identification?.[IdentificationPriority.PRIMARY]?.type ===
						'passport'
						? client.identification?.[IdentificationPriority.PRIMARY]
						: client.identification?.[IdentificationPriority.SECONDARY];

				if (!isSet(_formData?.[ClientSSProperties[index].dateIdVerified])) {
					_formData[ClientSSProperties[index].dateIdVerified] =
						Identification.dateOfVerification
							? format(
									GetDateObject(Identification.dateOfVerification),
									'MMMM dd, yyyy',
								)
							: '';
				}
				if (!isSet(_formData?.[ClientSSProperties[index].dateOfBirth])) {
					_formData[ClientSSProperties[index].dateOfBirth] = format(
						GetDateObject(client.dateOfBirth),
						'MMMM dd, yyyy',
					);
				}
				if (!isSet(_formData?.[ClientSSProperties[index].ssFullNameOnId])) {
					_formData[ClientSSProperties[index].ssFullNameOnId] =
						Identification.fullName;
				}
				if (
					!isSet(_formData?.[ClientSSProperties[index].identificationNumber])
				) {
					_formData[ClientSSProperties[index].identificationNumber] =
						Identification.number;
				}
				if (
					!isSet(
						_formData?.[ClientSSProperties[index].typeOfPhotoIdentification],
					)
				) {
					_formData[ClientSSProperties[index].typeOfPhotoIdentification] =
						Identification.type;
				}
				if (
					!isSet(_formData?.[ClientSSProperties[index].expiryDateIfApplicable])
				) {
					_formData[ClientSSProperties[index].expiryDateIfApplicable] = format(
						GetDateObject(Identification.expirationDate),
						'MMMM dd, yyyy',
					);
				}
				if (!isSet(_formData?.[ClientSSProperties[index].issuingCountry])) {
					_formData[ClientSSProperties[index].issuingCountry] =
						Identification.country;
				}
				if (
					!isSet(_formData?.[ClientSSProperties[index].issuingJurisdiction])
				) {
					_formData[ClientSSProperties[index].issuingJurisdiction] =
						Identification.jurisdiction;
				}
			}

			// select a contact for ds method
			// if they don't have dl or passport
			const DSContacts = Contacts.filter(
				(c) => !SSContacts.find((ssContact) => ssContact.id === c.id),
			);

			const ClientDSProperties = [
				{
					dsDateIdVerifiedIndividual1: 'dsDateIdVerifiedIndividual1',
					dsName1: 'dsName1',
					dsName2: 'dsName2',
					dsAddress1: 'dsAddress1',
					dsAddress2: 'dsAddress2',
					dsDateOfBirth1: 'dsDateOfBirth1',
					dsDateOfBirth2: 'dsDateOfBirth2',
					dsIdentificationDocument1: 'dsIdentificationDocument1',
					dsIdentificationDocument2: 'dsIdentificationDocument2',
					dsIdentificationDocumentNumber1: 'dsIdentificationDocumentNumber1',
					dsIdentificationDocumentNumber2: 'dsIdentificationDocumentNumber2',
				},
				{
					dsDateIdVerifiedIndividual1: 'dsDateIdVerifiedIndividual2',
					dsName1: 'dsName3',
					dsName2: 'dsName4',
					dsAddress1: 'dsAddress3',
					dsAddress2: 'dsAddress4',
					dsDateOfBirth1: 'dsDateOfBirth3',
					dsDateOfBirth2: 'dsDateOfBirth4',
					dsIdentificationDocument1: 'dsIdentificationDocument3',
					dsIdentificationDocument2: 'dsIdentificationDocument4',
					dsIdentificationDocumentNumber1: 'dsIdentificationDocumentNumber3',
					dsIdentificationDocumentNumber2: 'dsIdentificationDocumentNumber4',
				},
				{
					dsDateIdVerifiedIndividual1: 'dsIdVerifiedIndividual3',
					dsName1: 'dsName5',
					dsName2: 'dsName6',
					dsAddress1: 'dsAddress5',
					dsAddress2: 'dsAddress6',
					dsDateOfBirth1: 'dsDateOfBirth5',
					dsDateOfBirth2: 'dsDateOfBirth6',
					dsIdentificationDocument1: 'dsIdentificationDocument5',
					dsIdentificationDocument2: 'dsIdentificationDocument6',
					dsIdentificationDocumentNumber1: 'dsIdentificationDocumentNumber5',
					dsIdentificationDocumentNumber2: 'dsIdentificationDocumentNumber6',
				},
			];
			for (let index = 0; index < DSContacts.length && index <= 2; index++) {
				const client = DSContacts[index];
				// primary if exists goes to 1
				// secondary if exists goes to 2
				if (client.identification?.[IdentificationPriority.PRIMARY]) {
					const id = client.identification?.[IdentificationPriority.PRIMARY];

					if (
						!isSet(
							_formData?.[
								ClientDSProperties[index].dsDateIdVerifiedIndividual1
							],
						)
					) {
						_formData[ClientDSProperties[index].dsDateIdVerifiedIndividual1] =
							format(GetDateObject(id.dateOfVerification), 'MMMM dd, yyyy');
					}
					if (!isSet(_formData?.[ClientDSProperties[index].dsName1])) {
						_formData[ClientDSProperties[index].dsName1] = id.fullName;
					}
					if (!isSet(_formData?.[ClientDSProperties[index].dsAddress1])) {
						_formData[ClientDSProperties[index].dsAddress1] = addressString(
							client.serviceAddress,
						);
					}
					if (!isSet(_formData?.[ClientDSProperties[index].dsDateOfBirth1])) {
						_formData[ClientDSProperties[index].dsDateOfBirth1] = format(
							GetDateObject(client.dateOfBirth),
							'MMMM dd, yyyy',
						);
					}
					if (
						!isSet(
							_formData?.[ClientDSProperties[index].dsIdentificationDocument1],
						)
					) {
						_formData[ClientDSProperties[index].dsIdentificationDocument1] =
							id.type;
					}
					if (
						!isSet(
							_formData?.[
								ClientDSProperties[index].dsIdentificationDocumentNumber1
							],
						)
					) {
						_formData[
							ClientDSProperties[index].dsIdentificationDocumentNumber1
						] = id.number;
					}
				}

				if (client.identification?.[IdentificationPriority.SECONDARY]) {
					const id = client.identification?.[IdentificationPriority.SECONDARY];

					if (!isSet(_formData?.[ClientDSProperties[index].dsName2])) {
						_formData[ClientDSProperties[index].dsName2] = id.fullName;
					}
					if (!isSet(_formData?.[ClientDSProperties[index].dsAddress2])) {
						_formData[ClientDSProperties[index].dsAddress2] = addressString(
							client.serviceAddress,
						);
					}
					if (!isSet(_formData?.[ClientDSProperties[index].dsDateOfBirth2])) {
						_formData[ClientDSProperties[index].dsDateOfBirth2] = format(
							GetDateObject(client.dateOfBirth),
							'MMMM dd, yyyy',
						);
					}
					if (
						!isSet(
							_formData?.[ClientDSProperties[index].dsIdentificationDocument2],
						)
					) {
						_formData[ClientDSProperties[index].dsIdentificationDocument2] =
							id.type;
					}
					if (
						!isSet(
							_formData?.[
								ClientDSProperties[index].dsIdentificationDocumentNumber2
							],
						)
					) {
						_formData[
							ClientDSProperties[index].dsIdentificationDocumentNumber2
						] = id.number;
					}
				}
			}

			const FilteredMatterClients = matterClients.filter(
				(client) => !isGuarantor(client),
			);
			const CorporationClients: BusinessMatterClientWithAssociations[] = [];
			FilteredMatterClients.forEach((client) => {
				if (!clientIsPerson(client)) {
					CorporationClients.push(client);
				}
			});
			const ClientCorporationProperties = [
				{
					nameOfCorp1: 'nameOfCorp1',
					typeOfEntity: 'typeOfEntity',
				},
				{
					nameOfCorp1: 'nameOfCorp2',
					typeOfEntity: 'typeOfEntity2',
				},
			];
			for (
				let index = 0;
				index < CorporationClients.length && index <= 1;
				index++
			) {
				const client = CorporationClients[index];
				if (
					!isSet(_formData?.[ClientCorporationProperties[index].nameOfCorp1])
				) {
					_formData[ClientCorporationProperties[index].nameOfCorp1] =
						client.name;
				}
				if (
					!isSet(_formData?.[ClientCorporationProperties[index].typeOfEntity])
				) {
					_formData[ClientCorporationProperties[index].typeOfEntity] =
						'Corporation';
				}
			}
		}

		if (!isSet(_formData?.todaysDate2)) {
			_formData.todaysDate2 = format(new Date(), 'MMMM dd, yyyy');
		}

		if (matter?.lawyerAssigned?.name && !isSet(_formData?.lawyerName)) {
			_formData.lawyerName = matter.lawyerAssigned.name;
		}

		return _formData;
	}

	MapValues(): Partial<TD_FINAL_REPORT> {
		return {};
	}
}

const Mappings: TD_FINAL_REPORT = {
	todaysDate: 'todays date',
	lawyersName: 'lawfirm name',
	toTheTdBank: 'to the td bank',
	branchTransitNumber: 'branch transit number',
	lenderReferenceNumber: 'lender reference number',

	clientsNames1: 'clients names 1',
	guarantorsPage1: 'guarantors page 1',
	briefLegalDescriptionOfPropertyProperty1:
		'Brief Legal Description of Property Property 1',
	municipalAddressOfPropertyProperty1:
		'Municipal Address of Property Property 1',
	municipalityCityOrTown: 'municipality city or town',
	taxRollNumber: 'tax roll number',

	registrationDate: 'registration date',
	registrationNumber: 'registration number',
	pageliberframe: 'PageLiberFrame',
	bookfolioroll: 'BookFolioRoll',
	lROName: 'l r o name',
	mortgagePriority1: 'mortgage priority 1',
	mortgagePriority2: 'mortgage priority 2',
	titleInsurance: 'Title Insurance',
	titleInsuranceCompanyName: 'title insurance company name',
	policyNumber: 'policy number',
	mortgagePriority3: 'mortgage priority 3',
	insuredAmount: 'premium',
	issuedOnDate: 'issued on date',
	nameOfInsured: 'clients names 2',
	leasehold: 'Leasehold',
	feeSimple: 'Fee Simple',
	otherTextCoveredInPolicy: 'other text covered in policy',
	otherCheckboxCoveredInPolicy: 'other checkbox covered in policy',
	clientsName3: 'clients name 3',
	scheduleBExceptions: 'schedule B exceptions',

	netAdvanceAmount: 'net advance amount',
	mortgageInsurancePremium: 'mortgage insurance premium',
	pstGst: 'pst gst',
	mortgageApplicationFee: 'mortgage application fee',
	appraisalFee: 'appraisal fee',
	otherDisbursements: 'other disbursements',
	textboxRealEstateTaxes: 'textbox real estate taxes',
	checkboxRealEstateTaxes: 'checkbox real estate taxes',
	taxesHaveBeenPaid: 'taxes have been paid',
	textboxPropertyTaxRolls: 'textbox property tax rolls',
	checkboxPropertyTaxRolls: 'checkbox property tax rolls',
	textboxIndependentLegalAdvice: 'textbox independent legal advice',
	checkboxIndependantLegalAdvice: 'checkbox independant legal advice',

	fireInsuranceCompanyName: 'fire insurance company name',
	fireInsuranceExpiryDate: 'fire insurance expiry date',
	fireInsuranceCoverageAmount: 'fire insurance coverage amount',
	fireInsurancePolicyNumber: 'fire insurance policy number',
	replacementValue: 'replacement value',

	provinceName: 'province name',
	dateIdVerified1: 'Date ID verified1',
	dateOfBirth1: 'Date of Birth1',
	ssFullNameOnId1: 'ss full name on id1',
	identificationNumber1: 'Identification Number1',
	typeOfPhotoIdentification1: 'Type of Photo Identification1',
	expiryDateIfApplicable1: 'Expiry Date if applicable1',
	issuingCountry1: 'Issuing Country1',
	issuingJurisdiction1: 'Issuing Jurisdiction1',

	dateIdVerified2: 'Date ID verified2',
	dateOfBirth2: 'Date of Birth2',
	ssFullNameOnId2: 'ss full name on id2',
	identificationNumber2: 'Identification Number2',
	typeOfPhotoIdentification2: 'Type of Photo Identification2',
	expiryDateIfApplicable2: 'Expiry Date if applicable2',
	issuingCountry2: 'Issuing Country2',
	issuingJurisdiction2: 'Issuing Jurisdiction2',

	dateIdVerified3: 'Date ID verified3',
	dateOfBirth3: 'Date of Birth3',
	ssFullNameOnId3: 'ss full name on id3',
	identificationNumber3: 'Identification Number3',
	typeOfPhotoIdentification3: 'Type of Photo Identification3',
	expiryDateIfApplicable3: 'Expiry Date if applicable3',
	issuingCountry3: 'Issuing Country3',
	issuingJurisdiction3: 'Issuing Jurisdiction3',

	dsDateIdVerifiedIndividual1: 'ds date id verified individual1',
	dsName2: 'ds name2',
	dsName1: 'ds name1',
	dsAddress2: 'ds address2',
	dsAddress1: 'ds address1',
	dsDateOfBirth2: 'ds date of birth2',
	dsDateOfBirth1: 'ds date of birth1',
	dsFinancialAccount2: 'ds financial account2',
	dsFinancialAccount1: 'ds financial account1',
	dsFinancialAccount22: 'ds financial account22',
	dsFinancialAccount12: 'ds financial account12',
	dsIdentificationDocument2: 'ds identification document2',
	dsIdentificationDocument1: 'ds identification document1',
	dsSourceOfIdentificationDoc2: 'ds source of identification doc2',
	dsSourceOfIdentificationDoc1: 'ds source of identification doc1',
	dsIdentificationDocumentNumber2: 'ds identification document number2',
	dsIdentificationDocumentNumber1: 'ds identification document number1',

	dsDateIdVerifiedIndividual2: 'ds date id verified individual2',
	dsName4: 'ds name4',
	dsName3: 'ds name3',
	dsAddress4: 'ds address4',
	dsAddress3: 'ds address3',
	dsDateOfBirth4: 'ds date of birth4',
	dsDateOfBirth3: 'ds date of birth3',
	dsFinancialAccount4: 'ds financial account4',
	dsFinancialAccont3: 'ds financial accont3',
	dsFinancialAccount42: 'ds financial account42',
	dsFinancialAccount32: 'ds financial account32',
	dsIdentificationDocument4: 'ds identification document4',
	dsIdentificationDocument3: 'ds identification document3',
	dsSourceOfIdentificationDoc4: 'ds source of identification doc4',
	dsSourceOfIdentificationDoc3: 'ds source of identification doc3',
	dsIdentificationDocumentNumber4: 'ds identification document number4',
	dsIdentificationDocumentNumber3: 'ds identification document number3',

	dsIdVerifiedIndividual3: 'ds id verified individual3',
	dsName6: 'ds name6',
	dsName5: 'ds name5',
	dsAddress6: 'ds address6',
	dsAddress5: 'ds address5',
	dsDateOfBirth6: 'ds date of birth6',
	dsDateOfBirth5: 'ds date of birth5',
	dsFinancialAccount6: 'ds financial account6',
	dsFinancialAccount5: 'ds financial account5',
	dsFinancialAccount62: 'ds financial account62',
	dsFinancialAccount52: 'ds financial account52',
	dsIdentificationDocument6: 'ds identification document6',
	dsIdentificationDocument5: 'ds identification document5',
	dsSourceOfIdentificationDoc6: 'ds source of identification doc6',
	dsSourceOfIdentificationDoc5: 'ds source of identification doc5',
	dsIdentificationDocumentNumber6: 'ds identification document number6',
	dsIdentificationDocumentNumber5: 'ds identification document number5',

	nameOfCorp1: 'name of corp1',
	typeOfEntity: 'type of entity',
	typeOfRecordReferredTo: 'Type of Record Referred to',
	corpRegistrationNumber: 'corp registration number',
	sourceOfTheElectronicVersion1: 'source of the electronic version1',

	nameOfCorp2: 'name of corp2',
	typeOfEntity2: 'type of entity 2',
	typeOfRecordReferredTo2: 'Type of Record Referred to2',
	corpRegistrationNumber2: 'corp registration number2',
	sourceOfTheElectronicVersion2: 'source of the electronic version2',

	complied: 'complied',

	todaysDate2: 'todays date 2',
	lawyerName: 'lawyer name',
};

export interface TD_FINAL_REPORT {
	todaysDate: string;
	lawyersName: string;
	toTheTdBank: string;
	branchTransitNumber: string;
	lenderReferenceNumber: string;
	clientsNames1: string;
	guarantorsPage1: string;
	briefLegalDescriptionOfPropertyProperty1: string;
	municipalAddressOfPropertyProperty1: string;
	municipalityCityOrTown: string;
	taxRollNumber: string;
	registrationDate: string;
	registrationNumber: string;
	pageliberframe: string;
	bookfolioroll: string;
	lROName: string;
	mortgagePriority1: string;
	mortgagePriority2: string;
	titleInsurance: string;
	titleInsuranceCompanyName: string;
	policyNumber: string;
	mortgagePriority3: string;
	insuredAmount: string;
	issuedOnDate: string;
	nameOfInsured: string;
	leasehold: string;
	feeSimple: string;
	otherTextCoveredInPolicy: string;
	otherCheckboxCoveredInPolicy: string;
	clientsName3: string;
	scheduleBExceptions: string;
	netAdvanceAmount: string;
	mortgageInsurancePremium: string;
	pstGst: string;
	mortgageApplicationFee: string;
	appraisalFee: string;
	otherDisbursements: string;
	textboxRealEstateTaxes: string;
	checkboxRealEstateTaxes: string;
	taxesHaveBeenPaid: string;
	textboxPropertyTaxRolls: string;
	checkboxPropertyTaxRolls: string;
	textboxIndependentLegalAdvice: string;
	checkboxIndependantLegalAdvice: string;
	fireInsuranceCompanyName: string;
	fireInsuranceExpiryDate: string;
	fireInsuranceCoverageAmount: string;
	fireInsurancePolicyNumber: string;
	replacementValue: string;
	provinceName: string;
	dateIdVerified1: string;
	dateOfBirth1: string;
	ssFullNameOnId1: string;
	identificationNumber1: string;
	typeOfPhotoIdentification1: string;
	expiryDateIfApplicable1: string;
	issuingCountry1: string;
	issuingJurisdiction1: string;
	dateIdVerified2: string;
	dateOfBirth2: string;
	ssFullNameOnId2: string;
	identificationNumber2: string;
	typeOfPhotoIdentification2: string;
	expiryDateIfApplicable2: string;
	issuingCountry2: string;
	issuingJurisdiction2: string;
	dateIdVerified3: string;
	dateOfBirth3: string;
	ssFullNameOnId3: string;
	identificationNumber3: string;
	typeOfPhotoIdentification3: string;
	expiryDateIfApplicable3: string;
	issuingCountry3: string;
	issuingJurisdiction3: string;
	dsDateIdVerifiedIndividual1: string;
	dsName2: string;
	dsName1: string;
	dsAddress2: string;
	dsAddress1: string;
	dsDateOfBirth2: string;
	dsDateOfBirth1: string;
	dsFinancialAccount2: string;
	dsFinancialAccount1: string;
	dsFinancialAccount22: string;
	dsFinancialAccount12: string;
	dsIdentificationDocument2: string;
	dsIdentificationDocument1: string;
	dsSourceOfIdentificationDoc2: string;
	dsSourceOfIdentificationDoc1: string;
	dsIdentificationDocumentNumber2: string;
	dsIdentificationDocumentNumber1: string;

	dsDateIdVerifiedIndividual2: string;
	dsName4: string;
	dsName3: string;
	dsAddress4: string;
	dsAddress3: string;
	dsDateOfBirth4: string;
	dsDateOfBirth3: string;
	dsFinancialAccount4: string;
	dsFinancialAccont3: string;
	dsFinancialAccount42: string;
	dsFinancialAccount32: string;
	dsIdentificationDocument4: string;
	dsIdentificationDocument3: string;

	dsSourceOfIdentificationDoc4: string;
	dsSourceOfIdentificationDoc3: string;
	dsIdentificationDocumentNumber4: string;
	dsIdentificationDocumentNumber3: string;

	dsIdVerifiedIndividual3: string;
	dsName6: string;
	dsName5: string;
	dsAddress6: string;
	dsAddress5: string;
	dsDateOfBirth6: string;
	dsDateOfBirth5: string;
	dsFinancialAccount6: string;
	dsFinancialAccount5: string;
	dsFinancialAccount62: string;
	dsFinancialAccount52: string;
	dsIdentificationDocument6: string;
	dsIdentificationDocument5: string;

	dsSourceOfIdentificationDoc6: string;
	dsSourceOfIdentificationDoc5: string;
	dsIdentificationDocumentNumber6: string;
	dsIdentificationDocumentNumber5: string;

	nameOfCorp1: string;
	typeOfEntity: string;
	typeOfRecordReferredTo: string;
	corpRegistrationNumber: string;
	sourceOfTheElectronicVersion1: string;
	nameOfCorp2: string;
	typeOfEntity2: string;
	typeOfRecordReferredTo2: string;
	corpRegistrationNumber2: string;
	sourceOfTheElectronicVersion2: string;
	complied: string;
	todaysDate2: string;
	lawyerName: string;
}
